import blog_1 from "../../assets/blog-1.png";
import blog_2 from "../../assets/blog-2.jpg";
import blog_3 from "../../assets/blog-3.png";
const casestudydata = [
  {
    id: "01",
    slug: "autonomous-claims-orchestration-health-first",
    industry: "Healthcare & Life Sciences",
    capability: "AI-Led Business Process",
    title: "Autonomous Prior Authorization & Adjudication at Scale",
    summary:
      "Transitioning legacy manual clinical chart reviews into real-time FHIR event streams with autonomous worker nodes and human-in-the-loop fallback.",
    client: "Tier-1 Health Maintenance Organization",
    duration: "6 Months",
    readTime: "8 min read",
    image: blog_3, // replace with your imported image variable
    date: "May 2026",
    // Stats Bar
    stats: [
      { number: "74%", tagline: "Reduction in Claim Turnaround Time" },
      { number: "$14.2M", tagline: "Annual Operational Savings" },
      { number: "99.2%", tagline: "Statutory SLA Adherence" },
      { number: "< 1.4s", tagline: "Real-time Adjudication Latency" },
    ],

    content: {
      // 1. Introduction
      introduction:
        "As clinical documentation volumes exploded across 1,200 participating healthcare networks, our client faced severe administrative bottlenecks under federal CMS-0057-F guidelines. Clinical intake teams were inundated with unstructured PDF patient records, faxed diagnostics, and disparate HL7 clinical feeds that required costly manual review.",
      // 2. Challenge
      challenge: {
        introPara:
          "The core operational spine relied on a legacy monolithic claim processing engine that offered zero horizontal scalability and frequently locked transactional database rows during peak intraday submissions.",
        sections: [
          {
            heading: "Core Architectural Deficiencies",
            items: [
              "Over 85% of complex prior authorizations were diverted into manual exception queues due to fragile schema validation.",
              "Legacy SOAP integrations generated systemic thread starvation on mainframe backends during morning surges.",
              "Lack of deterministic decision logging made federal compliance reporting a multi-week audit nightmare.",
            ],
          },
        ],
        outroPara:
          "The client urgently needed an event-driven orchestration system that could safely evaluate clinical necessity while preventing neural hallucination risks.",
      },

      // 3. Solution
      solution: {
        introPara:
          "We engineered an event-driven orchestration mesh using Camunda 8 (Zeebe) backed by specialized autonomous micro-workers that ingest and parse multi-modal patient records via FHIR API envelopes.",
        sections: [
          {
            heading: "Key Implementation Components",
            items: [
              "Decoupled ingestion pipelines utilizing Kafka event queues to isolate the legacy record system from burst spikes.",
              "Autonomous OCR and LLM reasoning workers constrained inside discrete, idempotent Zeebe jobs.",
              "Deterministic DMN (Decision Model and Notation) validation gates enforcing statutory compliance before writeback.",
              "Low-latency clinician review consoles with pre-highlighted diagnostic anomalies for low-confidence inferences.",
            ],
          },
        ],
        outroPara:
          "All clinical artifacts were parsed strictly in-memory using zero-retention policies to guarantee strict HIPAA and SOC 2 Type II data residency compliance.",
      },

      // 4. Result
      result: {
        introPara:
          "Within four months of rollout, the platform achieved touchless adjudication across 68% of standard intake requests, eliminating backlog queues completely.",
        sections: [
          {
            heading: "Operational & Architectural Impact",
            items: [
              "End-to-end processing turnaround decreased from 72 hours down to an average of 4.2 minutes.",
              "Clinical reviewer burnout dropped significantly as routine approvals were fully automated.",
              "Legacy database peak transaction load dropped by 58% due to asynchronous write-buffering.",
            ],
          },
        ],
        outroPara:
          "The enterprise successfully achieved 100% compliance with new CMS-0057-F mandate deadlines ahead of federal audits.",
      },

      // 5. Conclusion
      conclusion:
        "By treating cognitive AI models as bounded micro-workers rather than giving them unrestricted database control, the organization achieved true operational autonomy without sacrificing deterministic regulatory safety.",
    },
  },

  {
    id: "02",
    slug: "event-driven-wealth-management-core-replatform",
    industry: "Banking & Financial Services",
    capability: "Cloud",
    title: "Sub-Second Multi-Asset Clearing & Settlement Modernization",
    summary:
      "Modernizing an on-premise transactional ledger into a hybrid cloud event mesh with sub-millisecond settlement reconciliation.",
    client: "Global Institutional Investment Bank",
    duration: "9 Months",
    readTime: "7 min read",
    image: blog_1, // replace with your imported image variable
    date: "May 2026",
    stats: [
      { number: "3.8x", tagline: "Throughput Capacity Increase" },
      { number: "< 24ms", tagline: "p99 Event Stream Latency" },
      { number: "-65%", tagline: "Infrastructure Cloud TCO" },
      { number: "Zero", tagline: "Downtime Cutover Incidents" },
    ],
    content: {
      introduction:
        "Processing millions of trades daily across global exchanges, the institution's decades-old settlement pipeline struggled to meet increasingly strict T+1 clearing windows established by international securities regulators.",
      challenge: {
        introPara:
          "The existing infrastructure was anchored to on-premise monolithic batch servers that could only process settlement entries after markets closed, leaving high intraday exposure balances unresolved.",
        sections: [
          {
            heading: "Critical Bottlenecks Encountered",
            items: [
              "Batch window overruns frequently delayed trade reconciliations into the Asian market opening hours.",
              "Point-to-point database sync connections created cascading locks whenever trade volumes spiked beyond 25,000 TPS.",
              "Disaster recovery synchronization required hours of manual state comparisons and cold standby rebooting.",
            ],
          },
        ],
        outroPara:
          "The architectural mandate was straightforward: transition to real-time streaming settlement without taking any core production services offline.",
      },
      solution: {
        introPara:
          "Applying the Strangler Fig migration pattern, we deployed a hybrid-cloud event spine using Apache Kafka, Redis Cluster, and containerized Kubernetes services running on multi-region AWS infrastructure.",
        sections: [
          {
            heading: "Modernization Pillars",
            items: [
              "Debezium-based Change Data Capture (CDC) streaming state directly out of the legacy mainframe without performance penalty.",
              "Stateless microservices processing continuous double-entry ledger calculations in memory.",
              "Cross-cloud active-active data federation using CockroachDB for multi-region consistency.",
              "Automated canary deployments governed by strict end-to-end ledger verification suites.",
            ],
          },
        ],
        outroPara:
          "The entire dual-run phase executed silently for 90 days, comparing transaction parity in parallel until 100% accuracy was proven.",
      },
      result: {
        introPara:
          "The system eliminated batch clearing cycles completely, transitioning global desk transactions into near-continuous, sub-second clearing.",
        sections: [
          {
            heading: "Performance Milestones",
            items: [
              "Settlement risk exposure windows were compressed from 18 hours down to under 30 seconds.",
              "Peak stress-tested throughput surpassed 115,000 transactions per second at sub-24ms p99 latency.",
              "On-premise hardware footprints were consolidated, cutting annual infrastructure overhead by over 60%.",
            ],
          },
        ],
        outroPara:
          "The bank met all mandatory T+1 regulatory requirements two financial quarters ahead of industry deadlines.",
      },
      conclusion:
        "Modernizing mission-critical financial backends does not require high-risk 'big bang' rewrites; decoupled event pipelines allow legacy mainframes to run alongside modern cloud engines reliably.",
    },
  },
  {
    id: "03",
    slug: "predictive-telematics-iot-manufacturing-mesh",
    industry: "Manufacturing",
    capability: "Engineering",
    title: "Distributed Edge Computing for Connected Assembly Lines",
    summary:
      "Deploying edge Kubernetes clusters and sensor inference models to eliminate unscheduled assembly line downtime across 14 global plants.",
    client: "Global Automotive OEM",
    duration: "8 Months",
    readTime: "9 min read",
    image: blog_2, // replace with your imported image variable
    date: "May 2026",
    stats: [
      { number: "91%", tagline: "Predictive Equipment Failure Accuracy" },
      { number: "$22M", tagline: "Unplanned Outage Losses Prevented" },
      { number: "18ms", tagline: "Edge Decision Reaction Time" },
      { number: "14", tagline: "Global Facilities Connected" },
    ],

    content: {
      introduction:
        "Modern automotive manufacturing plants rely on thousands of synchronized robotics arms, CNC presses, and paint booths. An unexpected failure of a single drive gear can halt a facility line at an estimated cost of $22,000 per minute.",

      challenge: {
        introPara:
          "Plant sensors were capturing billions of telemetry data points daily, but the telemetry sat siloed inside isolated Programmable Logic Controllers (PLCs) or was transmitted too slowly to centralized cloud hubs.",
        sections: [
          {
            heading: "Manufacturing Bottlenecks",
            items: [
              "High network latency to central cloud regions made millisecond safety-trip decisions impossible.",
              "Bandwidth constraints prevented raw high-frequency vibration data from ever leaving the plant floor.",
              "Maintenance routines remained scheduled on calendar intervals rather than real-world mechanical wear indicators.",
            ],
          },
        ],
        outroPara:
          "The plant needed local intelligence capable of running machine learning inference on the factory floor without internet connectivity dependencies.",
      },
      solution: {
        introPara:
          "We engineered a distributed edge architecture using lightweight K3s Kubernetes clusters running directly inside industrial edge appliances beside factory cells.",
        sections: [
          {
            heading: "Technical Architecture",
            items: [
              "MQTT broker layers reading raw OPC-UA protocol telemetry at 1,000 readings per second.",
              "Optimized ONNX neural inference runtimes identifying high-frequency micro-vibrations before physical mechanical fatigue sets in.",
              "Autonomous local actuator cut-offs designed to slow equipment before catastrophic physical gear breaks occur.",
              "Asynchronous delta-sync uploading aggregated diagnostic summaries to the cloud data lake during off-peak hours.",
            ],
          },
        ],
        outroPara:
          "Edge clusters were secured with hardware-backed TPM 2.0 cryptographic modules to protect factory hardware from lateral network tampering.",
      },
      result: {
        introPara:
          "Within six months of full operational deployment, unplanned conveyor and robotics downtime dropped across all connected production lines.",
        sections: [
          {
            heading: "Business & Engineering Returns",
            items: [
              "Over 40 catastrophic equipment breakdowns were intercepted weeks before structural failures would have occurred.",
              "Preventive component replacements shifted entirely to planned maintenance shifts, preserving scheduled line quotas.",
              "Telemetry data backhaul costs were reduced by 82% through intelligent local edge aggregation.",
            ],
          },
        ],
        outroPara:
          "The solution established a shared engineering standard now implemented across all newly constructed manufacturing sites worldwide.",
      },

      conclusion:
        "True industrial intelligence demands moving compute to where the physical data originates; low-latency edge inference turns raw sensor noise into proactive enterprise resilience.",
    },
  },
];

export default casestudydata;
